/**
 * TanStack Query hooks for the team feature.
 *
 * Cache key convention:
 *  - team.members(includeInactive)
 *  - team.invitations(state)
 *  - team.collaborators(strategyId)
 *
 * Mutations invalidate the relevant key on success so the UI doesn't
 * have to refetch by hand. Errors surface via Sonner toasts at the
 * call site (the hooks themselves are silent — let the caller decide
 * the copy).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addCollaborator,
  cancelJoinRequest,
  changeMemberRole,
  createJoinRequest,
  decideJoinRequest,
  deleteOrganization,
  fetchCollaborators,
  fetchInvitations,
  fetchJoinRequests,
  fetchJoinTargets,
  fetchMembers,
  fetchOrganization,
  fetchReviewGrants,
  grantReviewAccess,
  leaveOrganization,
  removeCollaborator,
  removeMember,
  resendInvitation,
  revokeInvitation,
  revokeReviewAccess,
  sendInvitation,
  transferAdmin,
  updateCollaborator,
  updateOrganization,
} from './api';
import type {
  CollaboratorAddRequest,
  CollaboratorUpdateRequest,
  InvitationRequest,
  OrganizationUpdateRequest,
  OrgRole,
  ReviewGrantRequest,
} from './types';

export const teamKeys = {
  organization: () => ['team', 'organization'] as const,
  members: (includeInactive: boolean) =>
    ['team', 'members', { includeInactive }] as const,
  invitations: (state?: string) => ['team', 'invitations', { state }] as const,
  collaborators: (strategyId: string) =>
    ['team', 'collaborators', strategyId] as const,
  joinTargets: () => ['team', 'join-targets'] as const,
  reviewAccess: () => ['team', 'review-access'] as const,
  joinRequests: (state?: string) =>
    ['team', 'join-requests', { state }] as const,
};

export function useOrganization() {
  return useQuery({
    queryKey: teamKeys.organization(),
    queryFn: fetchOrganization,
    staleTime: 60_000,
  });
}

export function useUpdateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: OrganizationUpdateRequest) => updateOrganization(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamKeys.organization() });
    },
  });
}

export function useDeleteOrganization() {
  return useMutation({
    mutationFn: () => deleteOrganization(),
  });
}

export function useChangeMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      role,
    }: {
      userId: string;
      role: OrgRole;
    }) => changeMemberRole(userId, role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team', 'members'] });
    },
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => removeMember(userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team', 'members'] });
    },
  });
}

export function useTransferAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (targetUserId: string) => transferAdmin(targetUserId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team', 'members'] });
      qc.invalidateQueries({ queryKey: teamKeys.organization() });
    },
  });
}

export function useLeaveOrganization() {
  return useMutation({
    mutationFn: () => leaveOrganization(),
  });
}

export function useOrgMembers(includeInactive = false) {
  return useQuery({
    queryKey: teamKeys.members(includeInactive),
    queryFn: () => fetchMembers(includeInactive),
    staleTime: 30_000,
  });
}

export function useInvitations(state?: string) {
  return useQuery({
    queryKey: teamKeys.invitations(state),
    queryFn: () => fetchInvitations(state),
    staleTime: 15_000,
  });
}

export function useSendInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InvitationRequest) => sendInvitation(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team', 'invitations'] });
      qc.invalidateQueries({ queryKey: ['team', 'members'] });
    },
  });
}

export function useRevokeInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => revokeInvitation(invitationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team', 'invitations'] });
    },
  });
}

export function useResendInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => resendInvitation(invitationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team', 'invitations'] });
    },
  });
}

export function useStrategyCollaborators(strategyId: string | undefined) {
  return useQuery({
    queryKey: teamKeys.collaborators(strategyId ?? ''),
    queryFn: () => fetchCollaborators(strategyId!),
    enabled: !!strategyId,
    staleTime: 15_000,
  });
}

export function useAddCollaborator(strategyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CollaboratorAddRequest) =>
      addCollaborator(strategyId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamKeys.collaborators(strategyId) });
    },
  });
}

export function useUpdateCollaborator(strategyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      body,
    }: {
      userId: string;
      body: CollaboratorUpdateRequest;
    }) => updateCollaborator(strategyId, userId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamKeys.collaborators(strategyId) });
    },
  });
}

export function useRemoveCollaborator(strategyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => removeCollaborator(strategyId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamKeys.collaborators(strategyId) });
    },
  });
}


// ---------------------------------------------------------------------------
// Join requests
// ---------------------------------------------------------------------------

/**
 * Workspaces on the caller's own email domain that they could ask to join.
 *
 * Returns `domain: null` and an empty list for a consumer mailbox, which is
 * the common case — so callers should render nothing rather than an empty
 * state.
 */
export function useJoinTargets() {
  return useQuery({
    queryKey: teamKeys.joinTargets(),
    queryFn: fetchJoinTargets,
  });
}

export function useCreateJoinRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createJoinRequest,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamKeys.joinTargets() });
    },
  });
}

export function useCancelJoinRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: cancelJoinRequest,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamKeys.joinTargets() });
    },
  });
}

/** The admin queue. 403s for non-admins, so callers must gate on role. */
export function useJoinRequests(state?: string) {
  return useQuery({
    queryKey: teamKeys.joinRequests(state),
    queryFn: () => fetchJoinRequests(state),
  });
}

export function useDecideJoinRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      approve,
      decisionNote,
    }: {
      id: string;
      approve: boolean;
      decisionNote?: string | null;
    }) => decideJoinRequest(id, { approve, decisionNote }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['team', 'join-requests'] });
      // An approval adds a member — the members table is now stale.
      void qc.invalidateQueries({ queryKey: ['team', 'members'] });
    },
  });
}


// ---------------------------------------------------------------------------
// Review access
// ---------------------------------------------------------------------------

/** The workspace's review grants. Admin only: 403s for anyone else. */
export function useReviewGrants(enabled = true) {
  return useQuery({
    queryKey: teamKeys.reviewAccess(),
    queryFn: fetchReviewGrants,
    enabled,
    staleTime: 15_000,
  });
}

export function useGrantReviewAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ReviewGrantRequest) => grantReviewAccess(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamKeys.reviewAccess() });
      // Granting retires any pending ordinary invitation for the same address.
      void qc.invalidateQueries({ queryKey: ['team', 'invitations'] });
    },
  });
}

export function useRevokeReviewAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (grantId: string) => revokeReviewAccess(grantId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamKeys.reviewAccess() });
      void qc.invalidateQueries({ queryKey: ['team', 'invitations'] });
      void qc.invalidateQueries({ queryKey: ['team', 'members'] });
    },
  });
}
